import classNames from "classnames";
import { useFormik } from "formik";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import Input from "src/components/inputs/Input";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import DownwardIcon from "src/icons/DownwardIcon";
import RecentActivity from "src/tabs/dashboard/recent-activity/RecentActivity";
import { PR_STATUS } from "src/utils/constants";
import { number, object } from "yup";
import TotalAmountDetails from "../../../billing/create/TotalAmountDetails";
import {
  useCreatePurchaseOrders,
  useGetProcurementRequestSingle,
} from "../../api-purchase-orders";
import useCreatePurchaseOrderTableColumns from "./useCreatePurchaseOrderTableColumns";

const FORM_FIELDS = {
  DEADLINE: "deadline",
};

const CreatePurchaseOrders = ({
  isInViewMode = false,
}: {
  isInViewMode?: boolean;
}) => {
  useSetActiveTab(TAB_NAMES.PROCUREMENT_REQUEST);
  const params = useParams();
  const [length] = useState(10);
  const [page] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const [tableData, setTableData] = useState([]);
  const { successNotification, errorNotification } = useNotification();

  const {
    isLoading,
    error,
    data,
    mutate: updatePurchaseOrderData,
    isValidating,
  } = useGetProcurementRequestSingle({
    length,
    page,
    search: debouncedSearch,
    purchaseOrderId: params.purchaseOrderId,
  });

  const taxAndGST = useMemo(
    () => Number(data?.data?.tax ?? 0) + Number(data?.data?.gst ?? 0),
    [data?.data?.gst, data?.data?.tax]
  );

  const {
    isLoading: isPurchaseOrderLoading,
    isSuccess: createPurchaseOrderSucceeded,
    execute: createPurchaseOrder,
  } = useCreatePurchaseOrders({
    purchaseOrderId: params.purchaseOrderId ?? "",
    vendorId: data?.data?.vendorId,
  });

  const purchaseOrderData = useMemo(
    () => data?.data?.procurementRequestProducts ?? [],
    [data?.data]
  );

  const mappedPurchaseOrderData = useMemo(() => {
    return purchaseOrderData?.map((po: any) => ({
      totalPrice: po.quantity * po.offeredPrice,
      ...po,
    }));
  }, [purchaseOrderData]);

  useEffect(
    () => setTableData(mappedPurchaseOrderData),
    [mappedPurchaseOrderData]
  );

  useEffect(() => {
    if (createPurchaseOrderSucceeded) {
      successNotification("Created successfully");
      updatePurchaseOrderData();
    }
  }, [
    createPurchaseOrderSucceeded,
    successNotification,
    updatePurchaseOrderData,
  ]);

  const handleUnitPriceChange = (
    e: ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    const { value } = e.target;
    setTableData((prev: any) =>
      prev.map((item: any) =>
        id === item.productId
          ? {
              ...item,
              products: { ...item.products, price: value ?? 0 },
              totalPrice: item.quantity * (value ? parseInt(value) : 0),
            }
          : item
      )
    );
  };

  const subtotal = useMemo(
    () => tableData.reduce((acc, { totalPrice }) => totalPrice + acc, 0),
    [tableData]
  );

  const calculatedTax = useMemo(
    () => subtotal * (taxAndGST / 100),
    [subtotal, taxAndGST]
  );

  const columns = useCreatePurchaseOrderTableColumns({
    handleUnitPriceChange,
    status: data?.data?.status,
  });

  const canSubmit = useMemo(() => {
    return tableData.every((td: any) => !!td.products.price);
  }, [tableData]);

  const { values, errors, handleSubmit, setFieldValue } = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.DEADLINE]: data?.data?.[FORM_FIELDS.DEADLINE] ?? null,
    },
    validationSchema: object({
      [FORM_FIELDS.DEADLINE]: number().required("This is a Required field."),
    }),
    onSubmit: (data) => {
      if (!canSubmit) return;

      const payload = {
        deadline: data.deadline,
        products: tableData.map(
          ({ productId, quantity, products: { price } }) => ({
            id: productId,
            quantity,
            price: price,
          })
        ),
      };

      createPurchaseOrder(payload);
    },
  });

  // const handleSubmit = useCallback(() => {
  //   if (!canSubmit) return;

  //   const payload = {
  //     products: tableData.map(
  //       ({ productId, quantity, products: { price } }) => ({
  //         id: productId,
  //         quantity,
  //         price: price,
  //       })
  //     ),
  //   };

  //   createPurchaseOrder(payload);
  // }, [canSubmit, createPurchaseOrder, tableData]);

  return (
    <>
      <div className="flex flex-col">
        <TabHeader
          label="Procurement Request"
          onRefresh={() => updatePurchaseOrderData()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(purchaseOrderData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
        />
        <ReactTable
          className="!rounded-b-none mt-5"
          columns={columns}
          data={tableData}
          isLoading={isLoading}
        />
        <div className="flex gap-6 bg-white p-4 rounded-b-[14px] border-t-[0.1px] border-solid border-lightGrey md:flex-row flex-col-reverse">
          <div className="w-full">
            <RecentActivity icon={<DownwardIcon />} />
          </div>

          <div className="flex flex-col gap-[14px] w-full pt-5">
            <div className="text-16px font-semibold py-2.5 flex items-center gap-2">
              Delivery Deadline:
              {data?.data?.status === PR_STATUS.PENDING && !isInViewMode ? (
                <Input
                  name="deadline"
                  type="number"
                  label={""}
                  className="w-20 [&>div>div]:border-t-0 [&>div>div]:border-x-0"
                  value={values.deadline}
                  onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                    setFieldValue(FORM_FIELDS.DEADLINE, Number(target.value))
                  }
                  error={errors[FORM_FIELDS.DEADLINE] as string}
                  min={0}
                />
              ) : (
                <span>{values[FORM_FIELDS.DEADLINE]}</span>
              )}
              Days
            </div>
            <TotalAmountDetails subtotal={subtotal} gst={calculatedTax} />
            {data?.data?.status === PR_STATUS.PENDING && !isInViewMode && (
              <div className="flex gap-[14px] justify-end">
                <Button
                  text="Submit"
                  primary
                  className={classNames("w-full select-none", {
                    "pointer-events-none opacity-50": !canSubmit,
                    "cursor-pointer": canSubmit,
                  })}
                  onClick={handleSubmit}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreatePurchaseOrders;
